import type { AddressPrediction, ResolvedAddress } from "../../types/address";
import {
  loadGoogleMapsPlaces,
  readGooglePlaceLocation,
  type GooglePlacePrediction,
  type GooglePlacesLibrary,
} from "./googleMapsPlacesLoader";
import { promiseWithTimeout } from "./promiseWithTimeout";

export const ADDRESS_SEARCH_MIN_LENGTH = 3;
const MAX_RESULTS = 8;
const AUTOCOMPLETE_TIMEOUT_MS = 6_000;
const PLACE_DETAILS_TIMEOUT_MS = 4_000;

export type AddressSearchSession = {
  token: object | null;
};

export type AddressPredictionWithPlace = AddressPrediction & {
  toPlace: GooglePlacePrediction["toPlace"];
};

function predictionText(prediction: GooglePlacePrediction): string {
  return prediction.text?.text?.trim() ?? "";
}

function predictionMainText(prediction: GooglePlacePrediction): string {
  const main = prediction.mainText?.text?.trim();
  if (main) {
    return main;
  }

  const text = predictionText(prediction);
  return text.split(",")[0]?.trim() || text;
}

function toAddressPrediction(
  prediction: GooglePlacePrediction,
): AddressPredictionWithPlace | null {
  const text = predictionText(prediction) || predictionMainText(prediction);
  if (!prediction.placeId || !text) {
    return null;
  }

  return {
    placeId: prediction.placeId,
    text,
    mainText: predictionMainText(prediction),
    secondaryText: prediction.secondaryText?.text?.trim() || undefined,
    toPlace: prediction.toPlace,
  };
}

async function ensureSessionToken(
  placesLib: GooglePlacesLibrary,
  session: AddressSearchSession,
): Promise<object> {
  if (!session.token) {
    session.token = new placesLib.AutocompleteSessionToken();
  }
  return session.token;
}

export async function fetchAddressPredictions(params: {
  googleMapsApiKey: string;
  query: string;
  session: AddressSearchSession;
}): Promise<AddressPredictionWithPlace[]> {
  const query = params.query.trim();
  if (query.length < ADDRESS_SEARCH_MIN_LENGTH) {
    return [];
  }

  const placesLib = await loadGoogleMapsPlaces(params.googleMapsApiKey);
  const sessionToken = await ensureSessionToken(placesLib, params.session);

  const { suggestions } = await promiseWithTimeout(
    placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: query,
      sessionToken,
    }),
    AUTOCOMPLETE_TIMEOUT_MS,
    "Address autocomplete timed out",
  );

  return suggestions
    .map((suggestion) => suggestion.placePrediction)
    .filter(
      (prediction): prediction is GooglePlacePrediction => prediction != null,
    )
    .map(toAddressPrediction)
    .filter((prediction): prediction is AddressPredictionWithPlace => {
      return prediction != null;
    })
    .slice(0, MAX_RESULTS);
}

export async function resolveAddressPlace(
  prediction: AddressPredictionWithPlace,
): Promise<ResolvedAddress | null> {
  const place = prediction.toPlace();
  try {
    await promiseWithTimeout(
      place.fetchFields({ fields: ["formattedAddress", "location"] }),
      PLACE_DETAILS_TIMEOUT_MS,
      "Place details timed out",
    );
  } catch {
    return null;
  }

  const coords = readGooglePlaceLocation(place);
  if (!coords) {
    return null;
  }

  const formattedAddress =
    place.formattedAddress?.trim() || prediction.text.trim();
  if (!formattedAddress) {
    return null;
  }

  return {
    placeId: prediction.placeId,
    formattedAddress,
    lat: coords.lat,
    lng: coords.lng,
  };
}
